import React, { useState, useEffect } from 'react';
import { FolderOpen, Plus, Edit2, Trash2, Save, X, Package } from 'lucide-react';
import api from '../api';

export default function ProductCategories() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ name: '', description: '' });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const data = await api.request('/api/product-categories');
      if (data.length === 0) {
        // Auto-seed from existing products
        await api.request('/api/product-categories/seed-from-products', { method: 'POST' });
        const seeded = await api.request('/api/product-categories');
        setCategories(seeded);
      } else {
        setCategories(data);
      }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleNew = () => {
    setEditingId(null);
    setForm({ name: '', description: '' });
    setShowForm(true);
  };

  const handleEdit = (cat) => {
    setEditingId(cat.id);
    setForm({ name: cat.name, description: cat.description || '' });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { alert('Category name is required'); return; }
    setSaving(true);
    try {
      if (editingId) {
        await api.request(`/api/product-categories/${editingId}`, { method: 'PUT', body: JSON.stringify(form) });
      } else {
        await api.request('/api/product-categories', { method: 'POST', body: JSON.stringify(form) });
      }
      setShowForm(false);
      setEditingId(null);
      load();
    } catch (err) { alert('Failed: ' + err.message); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this category?')) return;
    try {
      await api.request(`/api/product-categories/${id}`, { method: 'DELETE' });
      load();
    } catch (err) { alert('Failed: ' + err.message); }
  };

  return (
    <div className="space-y-6 max-w-3xl" data-testid="product-categories-page">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-semibold tracking-tight">Product Categories</h2>
          <p className="text-muted-foreground mt-1">Manage categories for product organization. Changes apply to all products in the category.</p>
        </div>
        <button onClick={handleNew} className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90" data-testid="new-category-btn">
          <Plus className="h-4 w-4" /> New Category
        </button>
      </div>

      {/* Form */}
      {showForm && (
        <div className="bg-card border border-border rounded-lg p-5 space-y-4" data-testid="category-form">
          <h3 className="font-semibold">{editingId ? 'Edit Category' : 'New Category'}</h3>
          <div className="grid grid-cols-1 gap-4">
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Category Name *</label>
              <input type="text" value={form.name} onChange={e => setForm({...form, name: e.target.value})}
                placeholder="e.g. Supplies, Motor Parts" className="w-full px-3 py-2 border border-input rounded-md text-sm" data-testid="category-name-input" />
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Description</label>
              <input type="text" value={form.description} onChange={e => setForm({...form, description: e.target.value})}
                placeholder="Optional description" className="w-full px-3 py-2 border border-input rounded-md text-sm" data-testid="category-description-input" />
            </div>
          </div>
          {editingId && (
            <p className="text-xs text-amber-600 bg-amber-50 px-3 py-2 rounded">Renaming this category will update the category on ALL products that use it.</p>
          )}
          <div className="flex gap-2 pt-2">
            <button onClick={handleSave} disabled={saving}
              className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90 disabled:opacity-50" data-testid="save-category-btn">
              <Save className="h-4 w-4" /> {saving ? 'Saving...' : 'Save'}
            </button>
            <button onClick={() => { setShowForm(false); setEditingId(null); }} className="px-4 py-2 border rounded-md hover:bg-muted">
              <X className="h-4 w-4 inline mr-1" /> Cancel
            </button>
          </div>
        </div>
      )}

      {/* Category List */}
      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/30">
            <tr>
              <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Category</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Description</th>
              <th className="text-right px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Products</th>
              <th className="text-center px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {loading ? (
              <tr><td colSpan="4" className="px-4 py-8 text-center text-muted-foreground">Loading...</td></tr>
            ) : categories.length === 0 ? (
              <tr><td colSpan="4" className="px-4 py-8 text-center text-muted-foreground">No categories yet</td></tr>
            ) : categories.map(cat => (
              <tr key={cat.id} className="hover:bg-muted/20" data-testid={`category-row-${cat.id}`}>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <FolderOpen className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium">{cat.name}</span>
                  </div>
                </td>
                <td className="px-4 py-3 text-muted-foreground">{cat.description || '\u2014'}</td>
                <td className="px-4 py-3 text-right">
                  <span className="flex items-center justify-end gap-1 text-muted-foreground">
                    <Package className="h-3.5 w-3.5" /> {cat.product_count}
                  </span>
                </td>
                <td className="px-4 py-3 text-center">
                  <div className="flex items-center justify-center gap-1">
                    <button onClick={() => handleEdit(cat)} className="p-1.5 hover:bg-muted rounded-md" data-testid={`edit-category-${cat.id}`}>
                      <Edit2 className="h-3.5 w-3.5 text-muted-foreground" />
                    </button>
                    <button onClick={() => handleDelete(cat.id)} className="p-1.5 hover:bg-red-50 rounded-md" data-testid={`delete-category-${cat.id}`}>
                      <Trash2 className="h-3.5 w-3.5 text-red-400" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
